import { alpha, type Theme } from '@mui/material/styles';

const tileWidth = 135.4;
export const tileFolderWidth = 76;
// The room a column of the icon view leaves on either side of its folder, which stands in its middle.
const tileSlack = (tileWidth - tileFolderWidth) / 2;
// How far in from the items pane's edge its contents start: the search box, a section's name,
// the first folder and the list's first column all keep to this one line.
const paneInset = 32;
// The lines between the window's parts: its toolbar, its tree and the head of its items.
const windowLine = alpha('#fff', 0.12);

/**
 * The window the folder is shown in, as a file manager shows one: a toolbar, and under it the
 * tree beside the items. It fills the screen however few the items are, less what stands above
 * it (the page's header, 93px, and the app bar's 64px below `lg`) and 24px under it, the margin
 * the layout keeps at its sides; the items scroll inside it, so the toolbar and the tree stay in view.
 * The layout keeps 40px under every page, so the window takes back the 16 it does not leave, or
 * the page would scroll by them.
 */
export const windowStyles = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: { xs: 'calc(100vh - 181px)', lg: 'calc(100vh - 117px)' },
  minHeight: 420,
  mb: '-16px',
  overflow: 'hidden',
};

export const toolbarStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  px: 1,
  py: 0.75,
  borderBottom: '1px solid',
  borderColor: windowLine,
};

export const toolbarGroupStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 0.5,
};

/** A toolbar's action: its icon and its word on no ground, quieter than the page's buttons. */
export const toolbarButtonStyles = {
  px: 1.5,
  color: 'text.secondary',
  textTransform: 'none',
  fontSize: 13.5,
  fontWeight: 500,
  // MUI pulls an icon 4px into the padding beside it, which put the icon nearer the button's edge
  // than the word at the other end is to its own.
  '& .MuiButton-startIcon': {
    ml: 0,
  },
  '& .MuiButton-endIcon': {
    mr: 0,
  },
  '&:hover': {
    color: 'text.primary',
    backgroundColor: alpha('#fff', 0.06),
  },
};

/** A toolbar's action shown by its icon alone, as tall as the ones with a word. */
export const toolbarIconButtonStyles = {
  width: 36,
  height: 36,
  borderRadius: 1,
  color: 'text.secondary',
  '&:hover': {
    color: 'text.primary',
    backgroundColor: alpha('#fff', 0.06),
  },
};

export const windowBodyStyles = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
};

/** The tree beside the items; a narrow screen leaves it out and keeps the room for them. */
export const treePaneStyles = {
  display: { xs: 'none', md: 'block' },
  width: 240,
  flexShrink: 0,
  overflow: 'auto',
  p: 1,
  borderRight: '1px solid',
  borderColor: windowLine,
};

export const treeListStyles = {
  listStyle: 'none',
  m: 0,
  p: 0,
};

export const treeItemStyles = {
  display: 'block',
};

/** A folder of the tree: the button that opens or closes it, then the folder, which is a link. */
export const treeRowStyles = {
  display: 'flex',
  alignItems: 'center',
  mb: '2px',
  '& .MuiListItemButton-root': {
    flex: 1,
    minWidth: 0,
    gap: 1,
    py: 0.5,
    px: 0.75,
    borderRadius: '6px',
    color: 'text.secondary',
    '& .MuiTypography-root': {
      fontSize: 13.5,
    },
    // `is-active` while the folder's menu is open.
    '&:hover, &.is-active': {
      backgroundColor: alpha('#fff', 0.05),
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: 'text.primary',
      backgroundColor: alpha('#fff', 0.1),
    },
  },
};

export const treeChevronStyles = {
  width: 24,
  height: 24,
  flexShrink: 0,
  p: 0,
  border: 'none',
  color: 'text.secondary',
  backgroundColor: 'transparent',
  '& svg': {
    transition: 'transform 120ms ease',
  },
};

export const contentPaneStyles = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
};

/** The head of the items: the search box, and how many items the folder holds. */
export const contentHeadStyles = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  px: `${paneInset}px`,
  py: 0.75,
  borderBottom: '1px solid',
  borderColor: windowLine,
};

export const contentCountStyles = {
  fontSize: 13,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
};

/**
 * Where the items scroll. A right-click anywhere in it, under the items too, is the folder's. A
 * container for the list's columns, which go by the width it has rather than the window's: the
 * tree beside it takes its share from a window of any size.
 */
export const contentScrollStyles = {
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
};

/** A folder that is not there, or a search that found nothing, says so in the middle of the room the items would take. */
export const emptyMessageStyles = {
  m: 'auto',
  p: 3,
  textAlign: 'center',
  color: 'text.secondary',
};

/** On the line under the head of the items, so nothing moves while it shows. */
export const loadingBarStyles = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: -1,
};

/**
 * The icon view: one box holding its sections one under the other, and the only place their
 * spacing is set. A column starts short of the pane's line by the room beside its folder, so
 * the folder itself starts on the line; a name wider than its folder still keeps clear of the pane's edge.
 * The last column ends the same way short of the far line, where the head's item count ends.
 */
export const gridSectionsStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  py: 2,
  px: `${paneInset - tileSlack}px`,
};

/** A section: its name close over its items, so it reads as theirs. */
export const gridSectionStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
};

/**
 * The section's name, small and quiet as Finder and Drive name theirs. A folder stands in the
 * middle of its column, so the name is moved in by the room beside it and starts on the pane's
 * line, where the first folder does.
 */
export const gridSectionTitleStyles = {
  pl: `${tileSlack}px`,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.25,
  color: 'text.secondary',
};

export const gridStyles = {
  display: 'grid',
  // A column is wider than its folder, so a name of a few words stays on one line under it. A
  // folder stands in the middle of its column, so a name wider than the folder stays in the box.
  gridTemplateColumns: `repeat(auto-fill, ${tileWidth}px)`,
  // What a row has left over goes between its columns rather than after the last, so the
  // folders fill the pane from line to line at any width. Every section has as many columns
  // as the next, so their folders stand under one another.
  justifyContent: 'space-between',
  rowGap: '12px',
};

/** One item of the icon view: an icon with its name under it, and no box around them. */
export const tileStyles = {
  minWidth: 0,
  '& .tile-icon': {
    transition: 'background-color 120ms ease',
  },
  '&:hover .tile-icon': {
    backgroundColor: alpha('#fff', 0.05),
  },
};

/** The item whose menu is open. */
export const tileSelectedStyles = {
  '& .tile-icon, &:hover .tile-icon': {
    backgroundColor: alpha('#fff', 0.1),
  },
  '& .tile-name': {
    color: '#fff',
    backgroundColor: 'primary.main',
  },
};

/** The part of a tile that opens the item. Its menu opens with a right-click or a long press. */
export const tileBodyStyles = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  outline: 'none',
  '&:focus-visible .tile-icon': {
    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
  },
});

// Close about the icon, so its name sits near it: an item's own parts are the closest things on the page.
export const tileIconStyles = {
  width: 92,
  height: 72,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
};

/**
 * A project's picture in the icon view, framed so a dark one still shows its edge. As wide as a
 * folder, so pictures and folders keep to the same line under a section's name.
 */
export const tileThumbnailStyles = {
  width: tileFolderWidth,
  height: tileFolderWidth * 0.75,
  borderRadius: '6px',
  overflow: 'hidden',
  backgroundColor: '#1F2229',
  border: '1px solid',
  borderColor: alpha('#fff', 0.12),
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
};

export const tileNameStyles = {
  mt: 0.25,
  px: 0.75,
  py: 0.125,
  maxWidth: '100%',
  borderRadius: '4px',
  fontSize: 12.5,
  lineHeight: 1.4,
  textAlign: 'center',
  wordBreak: 'break-word',
  // Two lines, then an ellipsis: long project names are common and a tile has no room for more.
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

export const tileInfoStyles = {
  mt: 0.25,
  px: 0.5,
  maxWidth: '100%',
  fontSize: 12,
  textAlign: 'center',
  color: 'text.secondary',
};

export const tableStyles = {
  // Fixed, so the name column takes what the others leave and a long name ends in an ellipsis.
  tableLayout: 'fixed',
  '& .MuiTableCell-root': {
    borderColor: 'divider',
  },
  // The first column and the last one shown keep to the pane's line, as the search box above them does.
  '& .col-name': {
    pl: `${paneInset}px`,
  },
  '& .col-created': {
    pr: `${paneInset}px`,
  },
  '& .row-info': {
    display: 'none',
  },
  // As the list narrows the columns go one by one, so the name keeps some 180 px of text beside its
  // icon: the date created, then the size, then the date modified, whose date and size the name then
  // carries under it. The steps are the other columns' widths (170, 120, 170) plus that room.
  '@container (max-width: 739px)': {
    '& .col-created': { display: 'none' },
    '& .col-size': { pr: `${paneInset}px` },
  },
  '@container (max-width: 569px)': {
    '& .col-size': { display: 'none' },
    '& .col-modified': { pr: `${paneInset}px` },
  },
  '@container (max-width: 449px)': {
    '& .col-modified': { display: 'none' },
    '& .col-name': { pr: `${paneInset}px` },
    '& .row-info': { display: 'block' },
    '& .row-id': { display: 'none' },
  },
  '& .MuiTableCell-head': {
    py: 1.25,
    fontSize: 12.5,
    fontWeight: 600,
    color: 'text.secondary',
    backgroundColor: '#15171C',
    whiteSpace: 'nowrap',
  },
  // The frame closes the last row; a section's last row keeps its line above the next section.
  '& .MuiTableBody-root:last-of-type .MuiTableRow-root:last-of-type .MuiTableCell-root': {
    borderBottom: 0,
  },
};

/**
 * The row that names a section of the list: a heading of the rows under it rather than a row
 * itself, so no line under it, little room below and more above, which sets the section apart.
 */
export const tableSectionRowStyles = {
  '& .MuiTableCell-root': {
    pt: 2.5,
    pb: 0.25,
    borderBottom: 0,
    fontSize: 12.5,
    fontWeight: 600,
    color: 'text.secondary',
  },
};

/**
 * A row as dense as a file list's: one line of name, or a name over its id, its location, or, in a
 * list too narrow for the columns, its date and size.
 */
export const tableRowStyles = (theme: Theme) => ({
  height: 48,
  cursor: 'pointer',
  outline: 'none',
  '&:focus-visible': {
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.light}`,
  },
  '& .MuiTableCell-body': {
    py: 0.5,
    fontSize: 13.5,
    color: 'text.secondary',
    whiteSpace: 'nowrap',
  },
});

export const tableNameStyles = {
  fontSize: 14,
  fontWeight: 500,
  color: 'text.primary',
};

export const tableSubtitleStyles = {
  fontSize: 12,
  color: 'text.secondary',
};

/**
 * The icon column of the list view: a folder's icon, or a project's picture in a small frame. As
 * wide as the picture, so every name starts at one place; the narrower folder keeps to the pane's line.
 */
export const tableIconStyles = {
  width: 40,
  height: 30,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
};

export const tableThumbnailStyles = {
  ...tableIconStyles,
  borderRadius: '6px',
  overflow: 'hidden',
  backgroundColor: '#1F2229',
  border: '1px solid',
  borderColor: 'divider',
};
